import { PRIVATE_MIRROR, feedKey } from '../lib/feedContext';
import { useSyncExternalStore } from 'react';

/**
 * Как открывать конторы: вкладки или половины, ось, держать окно, калькулятор.
 *
 * Это не AdsPower и не стол двух профилей. Профили — шаг `#ads`.
 * Здесь — то, что браузер умеет сам:
 * `window.open` с размером, повтор того же окна конторы, карточка вилки
 * третьим окном. THEFORKS делает это встроенным Chromium; мы не встроим
 * чужой сайт (X-Frame-Options). Честный аналог — два окна на половины.
 *
 * Ключ свой, не `forq.next.desk`: тот хранит тумблер AdsPower.
 */

export type DeskMode = 'auto' | 'split' | 'tabs';

/** side — слева и справа. stack — сверху и снизу, как у THEFORKS. */
export type SplitAxis = 'side' | 'stack';

/** card — калькулятор остаётся в карточке. pop — третье окно поверх. */
export type CalcPlace = 'card' | 'pop';

/** bar — узкий бар сканера. fork — окно практика (план / прибыль / добор). */
export type CalcSkin = 'bar' | 'fork';

export type DeskLayout = {
  mode: DeskMode;
  axis: SplitAxis;
  hold: boolean;
  calc: CalcPlace;
  calcSkin: CalcSkin;
  showCalculator: boolean;
  /** Карточка вилки вместе с конторами. Выключено — только выбранное расположение. */
  withFork: boolean;
  /**
   * Печатать сумму сканера в поле ставки. Выключено — исход выбирается,
   * сумму человек пишет сам. Даже включённая подстановка не перебивает
   * число, которое уже правят в купоне.
   */
  fillStake: boolean;
};

export const DEFAULT_LAYOUT: DeskLayout = {
  mode: 'auto',
  axis: 'side',
  hold: true,
  calc: 'card',
  calcSkin: 'bar',
  showCalculator: true,
  withFork: !PRIVATE_MIRROR,
  fillStake: true,
};

export const LAYOUT_KEY = PRIVATE_MIRROR ? feedKey('forq.next.desk-layout') : 'forq.next.desk-layout';

const MODES = ['auto', 'split', 'tabs'] as const;
const AXES = ['side', 'stack'] as const;
const CALCS = ['card', 'pop'] as const;
const SKINS = ['bar', 'fork'] as const;

let cached: DeskLayout | null = null;
const listeners = new Set<() => void>();
const emit = (): void => { listeners.forEach((listener) => listener()); };
const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

// Studio can be open in another tab. Invalidate even without React subscribers
// so imperative window-opening code also reads current preferences.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.storageArea !== null && event.storageArea !== localStorage) return;
    if (event.key !== null && event.key !== LAYOUT_KEY) return;
    cached = null;
    emit();
  });
}

export function useDeskLayout(): DeskLayout {
  return useSyncExternalStore(subscribe, loadLayout, () => DEFAULT_LAYOUT);
}

function one<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

export function normalizeLayout(raw: unknown): DeskLayout {
  const bag = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  return {
    mode: one(bag.mode, MODES, DEFAULT_LAYOUT.mode),
    axis: one(bag.axis, AXES, DEFAULT_LAYOUT.axis),
    hold: typeof bag.hold === 'boolean' ? bag.hold : DEFAULT_LAYOUT.hold,
    calc: one(bag.calc, CALCS, DEFAULT_LAYOUT.calc),
    calcSkin: one(bag.calcSkin, SKINS, DEFAULT_LAYOUT.calcSkin),
    showCalculator: typeof bag.showCalculator === 'boolean' ? bag.showCalculator : true,
    withFork: typeof bag.withFork === 'boolean' ? bag.withFork : DEFAULT_LAYOUT.withFork,
    fillStake: typeof bag.fillStake === 'boolean' ? bag.fillStake : true,
  };
}

export function loadLayout(): DeskLayout {
  if (cached !== null) return cached;
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    cached = raw === null ? { ...DEFAULT_LAYOUT } : normalizeLayout(JSON.parse(raw) as unknown);
  } catch {
    cached = { ...DEFAULT_LAYOUT };
  }
  return cached;
}

export function saveLayout(next: Partial<DeskLayout>): DeskLayout {
  const now = { ...loadLayout(), ...next };
  cached = now;
  try {
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(now));
  } catch {
    // Приватный режим — хватит памяти вкладки.
  }
  emit();
  return now;
}

/** Тесты не оставляют чужой расклад следующей проверке. */
export function resetLayout(): void {
  cached = null;
  try {
    localStorage.removeItem(LAYOUT_KEY);
  } catch {
    /* jsdom без storage */
  }
}
